import type { ClientContact } from "@jugl-web/rest-api/contacts";
import { ContactIntelligenceInfoSection } from "@web-src/modules/contacts/ContactsPage/components/ContactIntelligenceInfoSection";
import {
  hasVisitorIntelligenceData,
  mapContactToContactInfoData,
} from "@jugl-web/domain-resources/contacts/utils";
import { useTranslations } from "@jugl-web/utils";
import { FC, useMemo } from "react";
import { CRMChannel } from "@jugl-web/rest-api";
import { Tooltip } from "@jugl-web/ui-kit";
import { CustomerInfoCollapseSection } from "../CustomerInfoCollapseSection";
import { ReactComponent as ConfidenceIcon } from "./assets/confidence.svg";
import { ReactComponent as ProfileIcon } from "./assets/profile.svg";
import { ReactComponent as VisitorIntelligenceIcon } from "./assets/visitor-intelligence.svg";

export type CustomerProfileSectionsProps = {
  contact: Partial<ClientContact>;
  channelConnectionStatus?: Record<CRMChannel, boolean>;
  isVisitorIntelligenceVisible: boolean;
};

export const CustomerProfileSections: FC<CustomerProfileSectionsProps> = ({
  contact,
  channelConnectionStatus,
  isVisitorIntelligenceVisible,
}) => {
  const { t } = useTranslations();

  const data = useMemo(() => mapContactToContactInfoData(contact), [contact]);

  const hasVisitorIntelligence = hasVisitorIntelligenceData(contact);

  return (
    <div className="flex flex-col">
      <CustomerInfoCollapseSection
        title={t({
          id: "client-support-page.profile",
          defaultMessage: "Profile",
        })}
        icon={<ProfileIcon />}
        initialIsOpen
      >
        <div className="flex flex-col gap-5">
          <ContactIntelligenceInfoSection
            sectionId="profile"
            values={data.profile}
            hideHeader
          />
          {!!contact.conversations?.length && (
            <ContactIntelligenceInfoSection
              sectionId="channels"
              values={{ conversations: contact.conversations }}
              channelConnectionStatus={channelConnectionStatus}
              hideHeader
            />
          )}
        </div>
      </CustomerInfoCollapseSection>
      {isVisitorIntelligenceVisible && (
        <>
          <div className="my-7 h-px w-full shrink-0 bg-[#F2F2F2]" />
          <CustomerInfoCollapseSection
            title={t({
              id: "contacts-page.customer-info.visitor-intelligence",
              defaultMessage: "Visitor intelligence",
            })}
            icon={<VisitorIntelligenceIcon />}
            initialIsOpen={hasVisitorIntelligence}
            isCollapsible={hasVisitorIntelligence}
            endAdornment={
              hasVisitorIntelligence &&
              typeof data.visitorIntelligence.confidence === "number" ? (
                <Tooltip
                  renderTrigger={({ props: tooltipProps, ref }) => (
                    <div
                      className="flex h-[30px] cursor-default items-center gap-1.5 rounded-lg border border-solid border-grey-300 bg-white px-2.5"
                      ref={ref}
                      {...tooltipProps}
                    >
                      <ConfidenceIcon />
                      <span className="font-secondary text-[13px] font-medium text-dark-900">
                        {data.visitorIntelligence.confidence}/10
                      </span>
                    </div>
                  )}
                >
                  {t({
                    id: "contacts-page.confidence-tooltip",
                    defaultMessage:
                      "Confidence indicates how reliable the discovered visitor information may be. Lower scores can include incomplete or misleading info ℹ️",
                  })}
                </Tooltip>
              ) : undefined
            }
          >
            {hasVisitorIntelligence ? (
              <ContactIntelligenceInfoSection
                sectionId="visitorIntelligence"
                values={data.visitorIntelligence}
                hideHeader
              />
            ) : (
              <span className="font-secondary text-[13px] leading-[140%] tracking-[0.13px] text-dark-600">
                {t({
                  id: "client-support-page.visitor-intelligence-not-found",
                  defaultMessage:
                    "Visitor Intelligence info was not found for this User 🔍",
                })}
              </span>
            )}
          </CustomerInfoCollapseSection>
        </>
      )}
    </div>
  );
};
